import React, { ComponentPropsWithRef } from "react";
import {
  BlueBirdTheme, Divider, Typography, Loading,
} from "@kitbird/core";
import {
  InfoCard,
  LoadingContainer,
  UserPositions,
  PositionItem,
  UserInfo,
  StatusAndPoolRow,
  StyledBadge,
} from "./MutationUser.styled";

interface UserBadgeProps extends ComponentPropsWithRef<"div"> {
  color?: string;
  backgroundColor?: string;
  label?: string;
}

const UserBadge = ({
  color = BlueBirdTheme.colors.white,
  backgroundColor = BlueBirdTheme.colors.success[500],
  label = "",
  ...rest
}: UserBadgeProps) => (
  <StyledBadge $color={color} $backgroundColor={backgroundColor} {...rest}>
    <Typography level={6} fontWeight="regular">
      {label}
    </Typography>
  </StyledBadge>
);

interface UserPositionInfoProps<TUser extends {
  employee_id: string | number;
  employee_name: string;
  job_title: string;
}> {
  user?: TUser | null;
  userPosition?: {
    department: string;
    pool: string;
    status: string;
  }[];
  isLoading?: boolean;
  isError?: boolean;
  variant?: "DEFAULT" | "REPLACEMENT" | "CONFIRMATION" | "CONFIRMATION_INACTIVE" | "HISTORY";
  badge?: {
    show: boolean;
    label: string;
    color: string;
    backgroundColor: string;
    position: "bottom-left" | "top-right";
  }
}

const UserPositionInfo = <TUser extends {
  employee_id: string | number;
  employee_name: string;
  job_title: string;
}>({
    user,
    isLoading,
    isError,
    variant = "DEFAULT",
    badge = {
      color: BlueBirdTheme.colors.white,
      backgroundColor: BlueBirdTheme.colors.success[500],
      label: "",
      show: false,
      position: "bottom-left",
    },
    userPosition = [],
  }: UserPositionInfoProps<TUser>) => {
  const isReplacement = variant === "REPLACEMENT";
  const isConfirmation = variant === "CONFIRMATION";
  const isConfirmationInactive = variant === "CONFIRMATION_INACTIVE";
  const isHistory = variant === "HISTORY";

  const showSimple = isConfirmation || isConfirmationInactive || isHistory;
  const showUserPositions = !isConfirmation && !isConfirmationInactive && !isHistory;
  const showBadge = (position: "bottom-left" | "top-right") => badge && badge.show && badge.position === position;

  if (isLoading) {
    return (
      <InfoCard $isReplacement={isReplacement}>
        <LoadingContainer>
          <Loading variant="circular" />
        </LoadingContainer>
      </InfoCard>
    );
  }

  if (isError || !user) {
    return (
      <InfoCard $isReplacement={isReplacement}>
        <Typography color="danger" level={6}>Failed to load user information.</Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard
      $isReplacement={isReplacement}
      $invertColor={isConfirmationInactive}
      $minHeight={isHistory ? "auto" : undefined}
    >
      <UserInfo
        $isVerticallyCentered={isReplacement}
        $isSimple={showSimple}
      >
        <Typography color={BlueBirdTheme.colors.gray.subtitle} level={6} fontWeight="medium">
          NIP : {user.employee_id}
        </Typography>
        <Typography color={BlueBirdTheme.colors.gray.title} level={4} fontWeight="bold">
          {user.employee_name}
        </Typography>
        <Typography color={BlueBirdTheme.colors.primary[600]} level={6}>
          Jabatan : <span style={{ fontWeight: "500" }}>{user.job_title}</span>
        </Typography>
        {showBadge("bottom-left") && (
          <UserBadge
            label={badge.label}
            backgroundColor={badge.backgroundColor}
            color={badge.color}
            style={{ marginTop: "auto" }}
          />
        )}
      </UserInfo>
      {showBadge("top-right") && (
        <UserBadge
          label={badge.label}
          backgroundColor={badge.backgroundColor}
          color={badge.color}
          style={{ margin: "1.5rem 3.5rem 0 0" }}
        />
      )}
      {(showUserPositions) && (
        <UserPositions>
          <Typography color={BlueBirdTheme.colors.primary[600]} fontWeight="medium" level={6}>
            Posisi saat ini
          </Typography>
          {userPosition.map((pos, index: number) => (
            <React.Fragment key={index}>
              <PositionItem>
                <Typography level={7} color={BlueBirdTheme.colors.gray.subtitle} fontWeight="bold">
                  {pos.department}
                </Typography>
                <StatusAndPoolRow>
                  {isReplacement && (
                    <Typography level={7} color={BlueBirdTheme.colors.gray.subtitle} fontWeight="regular">
                      {pos.status}
                    </Typography>
                  )}
                  <Typography level={7} color={BlueBirdTheme.colors.gray.bodyText}>
                    {pos.pool}
                  </Typography>
                </StatusAndPoolRow>
              </PositionItem>
              {index < userPosition.length - 1 && (
                <Divider
                  flexItem
                  color={BlueBirdTheme.colors.gray.line}
                />
              )}
            </React.Fragment>
          ))}
        </UserPositions>
      )}
    </InfoCard>
  );
};

export default UserPositionInfo;
